import * as React from "react";
import { Box, Drawer, Stack, Toolbar, Typography } from "@mui/material";
import { RunningTotalsProps } from "../../../../types";

export interface KpiSidebarProps {
  runningTotals: RunningTotalsProps;
  drawerWidth: number;
  formatCurrency: (value: number) => string;
  exitStrategy: number;
  totalCost: number;
  totalCostBL: number;
  totalCostBP: number;
  profitUSD: number;
  profitMargin: number;
  varianceToExitStrategy: number;
  /** When true, renders "N/A" for NaN or Infinity variance (used by Create pages). */
  varianceNAGuard?: boolean;
  /** Optional top margin on the KPI Stack (Create pages use mt={2}). */
  stackMt?: number;
  /** Optional toolbar background colour (Create pages use "red"). */
  toolbarColor?: string;
  /** Spec-only KPI fields */
  paType?: "spec" | "sold";
  bpPrice?: number;
  bpProfit?: number;
  bpPercent?: number;
  blPrice?: number;
  blProfit?: number;
  blPercent?: number;
  exitPrice?: number;
  exitProfit?: number;
  exitPercent?: number;
}

function SpecKpiCell({ value, label }: { value: string; label: string }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", paddingX: 1, paddingY: 0.5 }}>
      <Typography variant="subtitle1" fontWeight="bold" align="right" sx={{ color: "#25D72C", lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Desktop permanent KPI drawer.  Not rendered on mobile — `ProfitAnalysisPage`
 * short-circuits to `<MobileShell>` below the md breakpoint and the mobile
 * KPI view lives in `MobileKpiView`.
 */
export function KpiSidebar({
  runningTotals,
  drawerWidth,
  formatCurrency,
  exitStrategy,
  totalCost,
  totalCostBL,
  totalCostBP,
  profitUSD,
  profitMargin,
  varianceToExitStrategy,
  varianceNAGuard = false,
  stackMt,
  toolbarColor,
  paType,
  bpPrice,
  bpProfit,
  bpPercent,
  blPrice,
  blProfit,
  blPercent,
  exitPrice,
  exitProfit,
  exitPercent,
}: KpiSidebarProps) {
  const varianceDisplay = varianceNAGuard
    ? isNaN(varianceToExitStrategy) || !isFinite(varianceToExitStrategy)
      ? "N/A"
      : `${varianceToExitStrategy.toFixed(2)}%`
    : `${varianceToExitStrategy.toFixed(2) || 0}%`;

  const specRows = [
    { label: "Exit Price", value: exitPrice !== undefined ? formatCurrency(exitPrice) : "N/A" },
    { label: "Exit Profit", value: exitProfit !== undefined ? formatCurrency(exitProfit) : "N/A" },
    { label: "Exit %", value: exitPercent !== undefined ? `${exitPercent.toFixed(2)}%` : "N/A" },
    { label: "BP Price", value: bpPrice !== undefined ? formatCurrency(bpPrice) : "N/A" },
    { label: "BP Profit", value: bpProfit !== undefined ? formatCurrency(bpProfit) : "N/A" },
    { label: "BP %", value: bpPercent !== undefined ? `${bpPercent.toFixed(2)}%` : "N/A" },
    { label: "BL Price", value: blPrice !== undefined ? formatCurrency(blPrice) : "N/A" },
    { label: "BL Profit", value: blProfit !== undefined ? formatCurrency(blProfit) : "N/A" },
    { label: "BL %", value: blPercent !== undefined ? `${blPercent.toFixed(2)}%` : "N/A" },
  ];

  const specGroups = [
    {
      label: "Exit",
      pct: exitPercent,
      price: exitPrice,
      profit: exitProfit,
    },
    {
      label: "BP",
      pct: bpPercent,
      price: bpPrice,
      profit: bpProfit,
    },
    {
      label: "BL",
      pct: blPercent,
      price: blPrice,
      profit: blProfit,
    },
  ];

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: "border-box",
        },
      }}
    >
      <Toolbar sx={toolbarColor ? { backgroundColor: toolbarColor } : undefined} />

      <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        {/* Hero */}
        <Box sx={{ px: 2, pt: 2.5, pb: 2, borderBottom: "0.5px solid", borderColor: "divider" }}>
          <Typography variant="caption" sx={{ letterSpacing: "0.09em", textTransform: "uppercase", color: "text.secondary", display: "block", mb: 0.5 }}>
            {paType === "spec" ? "Full Retail" : "Contract Price"}
          </Typography>
          <Typography variant="h5" fontWeight={600} sx={{ letterSpacing: "-0.03em", lineHeight: 1, color: "text.primary" }}>
            {paType === "spec" ? formatCurrency(runningTotals.fullRetail) : formatCurrency(runningTotals.contract)}
          </Typography>
        </Box>

        {/* Spec groups */}
        {paType === "spec" ? (
          <Box sx={{ px: 1.25, py: 1.5, display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
            {specGroups.map(({ label, pct, price, profit }) => (
              <Box key={label} sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                {/* Group header */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1.25, py: 0.75, backgroundColor: "rgba(0,0,0,0.03)", borderBottom: "0.5px solid", borderColor: "divider" }}>
                  <Typography variant="body2" fontWeight={600} sx={{ letterSpacing: "0.08em", textTransform: "uppercase", color: "text.secondary" }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ color: "#1a7a3f" }}>
                    {pct !== undefined ? `${pct.toFixed(2)}%` : "N/A"}
                  </Typography>
                </Box>
                {/* Price / Profit row */}
                <Box>
                  {[
                    { sublabel: "Price", val: price },
                    { sublabel: "Profit", val: profit },
                  ].map(({ sublabel, val }, i) => (
                    <Box key={sublabel} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", px: 1.25, py: 0.6, borderTop: i === 0 ? "none" : "0.5px solid", borderColor: "divider" }}>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        {sublabel}
                      </Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ letterSpacing: "-0.01em", color: "text.primary" }}>
                        {val !== undefined ? formatCurrency(val) : "N/A"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ px: 1.25, py: 1.5, display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
            {[
              { label: "Profit USD", value: formatCurrency(profitUSD) },
              { label: "Profit Margin", value: runningTotals.contract === 0 ? "N/A" : `${profitMargin.toFixed(2) || 0}%` },
              { label: "Variance To Exit Strategy", value: varianceDisplay },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", px: 1.25, py: 0.75 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", letterSpacing: "0.08em", textTransform: "capitalize" }}>
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ color: label === "Variance To Exit Strategy" ? (exitStrategy <= profitMargin ? "#25D72C" : "red") : "#25D72C", letterSpacing: "-0.01em" }}>
                    {value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Footer — Total Cost */}
        <Box sx={{ px: 2, py: 1.75, borderTop: "0.5px solid", borderColor: "divider" }}>
          <Typography variant="caption" sx={{ letterSpacing: "0.09em", textTransform: "uppercase", color: "text.secondary", display: "block", mb: 0.25 }}>
            Total Cost
          </Typography>
          {paType === "spec" ? (
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>BL</Typography>
                <Typography variant="body1" fontWeight={600} sx={{ letterSpacing: "-0.02em", color: "text.secondary" }}>
                  {formatCurrency(totalCostBL ?? 0)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>BP</Typography>
                <Typography variant="body1" fontWeight={600} sx={{ letterSpacing: "-0.02em", color: "text.secondary" }}>
                  {formatCurrency(totalCostBP ?? 0)}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body1" fontWeight={600} sx={{ letterSpacing: "-0.02em", color: "text.secondary" }}>
              {formatCurrency(totalCost)}
            </Typography>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
